import * as React from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/primitives/Button';

/**
 * ErrorBoundary - Catches React errors and displays fallback UI
 *
 * Per Phase 4 Plan 01:
 * - Class component (required by React error boundaries API)
 * - Integrates with Sentry for error reporting
 * - v7 Neon Party styled fallback UI
 * - Reset functionality to retry after error
 *
 * Usage:
 * - Wrap top-level Routes in App.tsx
 * - Wrap high-risk pages (EventRoom)
 * - Custom fallback UI via fallback prop
 */

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Custom fallback UI to show when error occurs */
  fallback?: React.ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  // Update state when error is thrown
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  // Log error to Sentry and call callback
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Report to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });

    // Call optional callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    console.error('ErrorBoundary caught error:', error, errorInfo);
  }

  // Reset error state to retry
  reset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI with v7 Neon Party styling
      return (
        <div className="min-h-screen flex items-center justify-center bg-v7-bg-primary px-4 py-8">
          <div className="max-w-md w-full text-center space-y-6">
            {/* Error icon */}
            <div className="text-6xl">⚠️</div>

            {/* Error title */}
            <div className="space-y-2">
              <h1 className="font-v7-headline font-bold text-2xl text-v7-orange-text">
                Something went wrong
              </h1>
              <p className="font-v7-body text-v7-text-secondary">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
            </div>

            {/* Error details card (development only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="p-4 bg-v7-bg-surface dark:bg-white/5 rounded-v7-lg border border-v7-border dark:border-white/10 text-left">
                <p className="font-mono text-xs text-v7-text-secondary break-all">
                  {this.state.error.stack}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3 pt-4">
              <Button variant="primary" fullWidth onClick={this.reset}>
                Try again
              </Button>
              <Button
                variant="tertiary"
                fullWidth
                onClick={() => window.location.href = '/'}
              >
                Go to homepage
              </Button>
            </div>

            {/* Help text */}
            <p className="font-v7-body text-xs text-v7-text-muted">
              If this keeps happening, please contact support
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
